import { SpeakersData as Speakers2023 } from '../data/2023/speakers.js'
import { SpeakersData as Speakers2024 } from '../data/2024/speakers.js'
import { SpeakersData as Speakers2025 } from '../data/2025/speakers.js'
import { SpeakersData as Speakers2026 } from '../data/2026/speakers.js'
import { SpeakersData as SpeakersLHM2026 } from '../data/2026/lhmSummit.js'

// Canonical aliases for known spelling variations across years
const KNOWN_ALIASES = {
  'madona-s-wambua': 'madona-wambua',
  'dave-koziel': 'dave-koziol',
  'w-scott-richardson': 'scott-richardson',
}

// Slugs that existed before credential stripping; kept so old links resolve
const LEGACY_SLUGS = {
  'ali-el-sharif-phd': 'ali-el-sharif',
  'bernadette-atanga-md': 'bernadette-atanga',
}

// Post-nominal credentials ("Ali El-Sharif, Ph.D.") are part of how a
// speaker presents themselves, not of who they are: one person, one profile.
const CREDENTIALS = /,\s*(Ph\.?\s?D\.?|M\.?D\.?|MBA|M\.?S\.?|P\.?E\.?|CPA)\s*$/i

export function splitCredentials(name = '') {
  const match = name.match(CREDENTIALS)
  return match
    ? { name: name.slice(0, match.index).trim(), credentials: match[1] }
    : { name: name.trim(), credentials: null }
}

const normalizeTitle = (title = '') =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

export function generateSlug(name) {
  if (!name) return ''
  // Normalize name by removing titles (Dr., Mr., Ms., etc.), credentials and middle initials
  const normalized = splitCredentials(name)
    .name.replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.|Prof\.)\s+/i, '')
    .replace(/\s+[A-Z]\.\s+/g, ' ')
    .trim()

  const baseSlug = normalized
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return KNOWN_ALIASES[baseSlug] || baseSlug
}

const rawData = [
  { year: 2023, data: Speakers2023 },
  { year: 2024, data: Speakers2024 },
  { year: 2025, data: Speakers2025 },
  { year: 2026, data: Speakers2026 },
  { year: 2026, data: SpeakersLHM2026 },
]

function buildRegistry() {
  const registry = new Map()

  rawData.forEach(({ year, data }) => {
    if (!Array.isArray(data)) return

    data.forEach((speaker) => {
      if (!speaker || !speaker.name) return
      const slug = generateSlug(speaker.name)
      const { name: baseName, credentials } = splitCredentials(
        speaker.name.replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.|Prof\.)\s+/i, '')
      )

      if (!registry.has(slug)) {
        registry.set(slug, {
          slug,
          name: baseName,
          credentials,
          avatar: speaker.avatar || null,
          avatarHistory: [],
          bio: speaker.bio || null,
          organization: speaker.organization || null,
          position: speaker.position || null,
          twitter: speaker.twitter || null,
          linkedIn: speaker.linkedIn || null,
          github: speaker.github || null,
          url: speaker.url || null,
          mastodon: speaker.mastodon || null,
          isGDE: speaker.isGDE || false,
          isWTM: speaker.isWTM || false,
          categories: new Set(),
          yearsActive: new Set(),
          sessions: [],
          slidesUrl: null,
          videoUrl: null,
          resources: [],
          talkPhotos: [],
          featured: false,
        })
      }

      const profile = registry.get(slug)

      // Prefer longer full name if present
      if (baseName.length > profile.name.length && !baseName.includes('.')) {
        profile.name = baseName
      }
      if (credentials) profile.credentials = credentials

      // Record avatar in history if available; generated placeholder images
      // (placehold.co) never replace a real photo and count as no photo
      const isPlaceholder = /placehold\.co/.test(speaker.avatar ?? '')
      if (isPlaceholder && !profile.avatarHistory.length) profile.avatar = null
      if (speaker.avatar && !isPlaceholder) {
        profile.avatar = speaker.avatar
        const exists = profile.avatarHistory.some((h) => h.year === year)
        if (!exists) {
          profile.avatarHistory.push({ year, avatar: speaker.avatar })
        }
      }

      if (speaker.bio && speaker.bio.length > (profile.bio || '').length)
        profile.bio = speaker.bio
      if (speaker.organization) profile.organization = speaker.organization
      if (speaker.position) profile.position = speaker.position
      if (speaker.twitter) profile.twitter = speaker.twitter
      if (speaker.linkedIn) profile.linkedIn = speaker.linkedIn
      if (speaker.github) profile.github = speaker.github
      if (speaker.url) profile.url = speaker.url
      if (speaker.mastodon) profile.mastodon = speaker.mastodon
      if (speaker.isGDE) profile.isGDE = speaker.isGDE
      if (speaker.isWTM) profile.isWTM = speaker.isWTM

      profile.yearsActive.add(year)

      // Normalize sessions array
      const sessions = Array.isArray(speaker.session)
        ? speaker.session
        : speaker.session
          ? [speaker.session]
          : []

      sessions.forEach((sess) => {
        if (sess) {
          // A talk offered in several tracks is one talk: merge the tracks
          const existing = profile.sessions.find(
            (s) =>
              s.year === year &&
              normalizeTitle(s.title) === normalizeTitle(sess.title)
          )
          if (existing) {
            if (sess.track && !existing.tracks.includes(sess.track))
              existing.tracks.push(sess.track)
            existing.tags = [
              ...new Set([...existing.tags, ...(sess.tags || [])]),
            ]
          } else {
            profile.sessions.push({
              year,
              title: sess.title,
              track: sess.track,
              tracks: sess.track ? [sess.track] : [],
              tags: sess.tags || [],
              abstract: sess.abstract,
              description: sess.description,
              time: sess.time,
              room: sess.room,
              event: sess.event,
            })
          }

          // Some sources pack several topics into one "A; B; C" string
          if (Array.isArray(sess.tags)) {
            sess.tags
              .flatMap((tag) => String(tag ?? '').split(/\s*[;|]\s*/))
              .forEach((tag) => {
                if (tag.trim()) profile.categories.add(tag.trim())
              })
          }
        }
      })
    })
  })

  // Convert Sets to Arrays and sort
  const finalRegistry = Array.from(registry.values()).map((profile) => ({
    ...profile,
    categories: Array.from(profile.categories).sort(),
    yearsActive: Array.from(profile.yearsActive).sort((a, b) => a - b),
    sessions: profile.sessions.sort((a, b) => b.year - a.year),
  }))

  // Sort alphabetically by name
  return finalRegistry.sort((a, b) => a.name.localeCompare(b.name))
}

const allSpeakers = buildRegistry()

export function getAllSpeakers() {
  return allSpeakers
}

export function getSpeakerBySlug(slug) {
  const canonical = LEGACY_SLUGS[slug] || slug
  return allSpeakers.find((s) => s.slug === canonical) || null
}

export function getSpeakersByCategory(category) {
  return allSpeakers.filter((s) =>
    s.categories.some((c) => c.toLowerCase() === category.toLowerCase())
  )
}

export function getSpeakersByYear(year) {
  return allSpeakers.filter((s) => s.yearsActive.includes(Number(year)))
}

export function getSpeakersByTrack(track) {
  return allSpeakers.filter((s) =>
    s.sessions.some((sess) => sess.tracks.includes(track))
  )
}

export function getAllCategories() {
  const cats = new Set()
  allSpeakers.forEach((s) => s.categories.forEach((c) => cats.add(c)))
  return Array.from(cats).sort((a, b) => a.localeCompare(b))
}

export function getAllTracks() {
  const tracks = new Set()
  allSpeakers.forEach((s) =>
    s.sessions.forEach((sess) => {
      sess.tracks.forEach((t) => tracks.add(t))
    })
  )
  return Array.from(tracks).sort((a, b) => a.localeCompare(b))
}

export function getTotalSpeakerCount() {
  return allSpeakers.length
}

export function getYearRange() {
  if (allSpeakers.length === 0) return { earliest: null, latest: null }
  const allYears = new Set()
  allSpeakers.forEach((s) => s.yearsActive.forEach((y) => allYears.add(y)))
  const yearsArr = Array.from(allYears)
  if (yearsArr.length === 0) return { earliest: null, latest: null }
  return {
    earliest: Math.min(...yearsArr),
    latest: Math.max(...yearsArr),
  }
}
